import React, { useState, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { AntiBot } from '../components/AntiBot';
import { motion } from 'framer-motion';
import { Anchor, Send, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuotePage = () => {
  const { t, language } = useLanguage();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    boat_info: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [antiBotValid, setAntiBotValid] = useState(false);
  const [formLoadTime] = useState(Date.now());
  const [captchaData, setCaptchaData] = useState({ answer: null, expected: null });

  const services = [
    { value: 'vendita', label: t('quote.services.vendita') },
    { value: 'invernaggio', label: t('quote.services.invernaggio') },
    { value: 'trasporto', label: t('quote.services.trasporto') },
    { value: 'manutenzione', label: t('quote.services.manutenzione') },
  ];

  // Anti-bot callback
  const handleAntiBotChange = useCallback((isValid, data) => {
    setAntiBotValid(isValid);
    if (data) {
      setCaptchaData(data);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!antiBotValid) {
      toast.error(language === 'it' ? 'Completa la verifica di sicurezza' : 
                  language === 'de' ? 'Bitte Sicherheitsprüfung abschließen' :
                  language === 'fr' ? 'Veuillez compléter la vérification de sécurité' :
                  'Please complete the security check');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/quote-requests`, {
        ...form,
        preferred_language: language,
        // Anti-bot data
        honeypot: '',
        formLoadTime: formLoadTime,
        captchaAnswer: captchaData.answer,
        captchaExpected: captchaData.expected
      });
      setSubmitted(true);
      toast.success(t('quote.success'));
    } catch (error) {
      toast.error(t('quote.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="quote-success" className="pt-20 min-h-screen flex items-center justify-center bg-[#F5F5F7]">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center p-12 max-w-lg"
        >
          <div className="w-20 h-20 bg-[#0B1221] rounded-full flex items-center justify-center mx-auto mb-8">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-[#0B1221] mb-4">
            {t('quote.success')}
          </h2>
          <p className="text-[#6E6E73] mb-8">
            {language === 'it' && 'Vi contatteremo il prima possibile per discutere le vostre esigenze.'}
            {language === 'de' && 'Wir werden Sie so schnell wie möglich kontaktieren, um Ihre Anforderungen zu besprechen.'}
            {language === 'fr' && 'Nous vous contacterons dès que possible pour discuter de vos besoins.'}
            {language === 'en' && 'We will contact you as soon as possible to discuss your requirements.'}
          </p>
          <Button
            onClick={() => {
              setSubmitted(false);
              setForm({
                name: '',
                email: '',
                phone: '',
                service_type: '',
                boat_info: '',
                message: ''
              });
            }}
            className="bg-[#0B1221] text-white hover:bg-[#1A2639] rounded-full px-8 py-4"
          >
            {language === 'it' ? 'Nuova richiesta' : language === 'de' ? 'Neue Anfrage' : language === 'fr' ? 'Nouvelle demande' : 'New request'}
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="quote-page" className="pt-20">
      {/* Hero */}
      <section className="py-24 bg-[#0B1221]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="w-16 h-16 bg-[#FF3B30] rounded-full flex items-center justify-center mx-auto mb-6">
              <Anchor className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              {t('quote.title')}
            </h1>
            <p className="text-[#86868B] text-lg max-w-2xl mx-auto">
              {t('quote.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quote Form */}
      <section className="py-16 lg:py-24">
        <div className="max-w-[600px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#0B1221] mb-2">
                  {t('contact.name')} *
                </label>
                <Input
                  data-testid="quote-name"
                  placeholder={t('contact.name')}
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  required
                  className="bg-[#F5F5F7] border-none py-4"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#0B1221] mb-2">
                    {t('contact.email')} *
                  </label>
                  <Input
                    data-testid="quote-email"
                    type="email"
                    placeholder={t('contact.email')}
                    value={form.email}
                    onChange={(e) => setForm({...form, email: e.target.value})}
                    required
                    className="bg-[#F5F5F7] border-none py-4"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0B1221] mb-2">
                    {t('contact.phone')} *
                  </label>
                  <Input
                    data-testid="quote-phone"
                    type="tel"
                    placeholder={t('contact.phone')}
                    value={form.phone}
                    onChange={(e) => setForm({...form, phone: e.target.value})}
                    required
                    className="bg-[#F5F5F7] border-none py-4"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0B1221] mb-2">
                  {t('quote.serviceType')} *
                </label>
                <Select 
                  value={form.service_type} 
                  onValueChange={(value) => setForm({...form, service_type: value})}
                  required
                >
                  <SelectTrigger data-testid="quote-service-type" className="bg-[#F5F5F7] border-none py-4">
                    <SelectValue placeholder={t('quote.serviceType')} />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((service) => (
                      <SelectItem key={service.value} value={service.value}>
                        {service.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0B1221] mb-2">
                  {t('quote.boatInfo')}
                </label>
                <Input
                  data-testid="quote-boat-info"
                  placeholder={t('quote.boatInfoPlaceholder')}
                  value={form.boat_info}
                  onChange={(e) => setForm({...form, boat_info: e.target.value})}
                  className="bg-[#F5F5F7] border-none py-4"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0B1221] mb-2">
                  {t('contact.message')} *
                </label>
                <Textarea
                  data-testid="quote-message"
                  placeholder={t('contact.message')}
                  value={form.message}
                  onChange={(e) => setForm({...form, message: e.target.value})}
                  required
                  className="bg-[#F5F5F7] border-none min-h-[150px]"
                />
              </div>

              {/* Anti-Bot Verification */}
              <AntiBot 
                language={language} 
                onValidChange={handleAntiBotChange}
              />

              <Button
                data-testid="quote-submit"
                type="submit"
                disabled={submitting || !form.service_type || !antiBotValid}
                className="w-full bg-[#0B1221] text-white hover:bg-[#1A2639] rounded-full py-4 text-sm font-medium disabled:opacity-50"
              >
                {submitting ? '...' : (
                  <>
                    {t('quote.submit')}
                    <Send className="ml-2 w-4 h-4" />
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
